import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { getAllLayouts, getLayoutById, getLayoutComponent, DEFAULT_LAYOUT_ID } from '../layouts/LayoutRegistry';

// Create context
const LayoutContext = createContext();

export const LayoutProvider = ({ children }) => {
  // State for the list of available layouts (metadata)
  const [layouts, setLayouts] = useState([]); // Initialize as empty array

  // State for the currently selected layout ID
  const [selectedLayout, setSelectedLayout] = useState(() => {
    // Load saved layout from localStorage or use default
    try {
      const savedLayoutId = localStorage.getItem('selectedLayoutId');
      // Validate if the saved ID still exists in the registry (optional but good practice)
      // For now, just return the saved ID or default
      return savedLayoutId || DEFAULT_LAYOUT_ID;
    } catch (error) {
      console.error("Error reading layout ID from localStorage:", error);
      return DEFAULT_LAYOUT_ID;
    }
  });

  // Effect to load layout metadata on mount
  useEffect(() => {
    try {
      const availableLayouts = getAllLayouts(); // Get layouts from registry
      setLayouts(availableLayouts);
      console.log('LayoutContext: Loaded layouts', availableLayouts);

      // Validate the currently selected layout ID exists
      const currentLayoutExists = availableLayouts.some(layout => layout.id === selectedLayout);
      if (!currentLayoutExists) {
        console.warn(`LayoutContext: Saved layout ID "${selectedLayout}" not found, resetting to default.`);
        setSelectedLayout(DEFAULT_LAYOUT_ID);
        localStorage.setItem('selectedLayoutId', DEFAULT_LAYOUT_ID);
      }

    } catch (error) {
      console.error("LayoutContext: Failed to load layouts from registry:", error);
      setLayouts([]); // Set to empty array on error
    }
  }, [selectedLayout]); // Re-run validation if selectedLayout changes externally? Maybe not needed.

  // Effect to save selected layout ID to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('selectedLayoutId', selectedLayout);
    } catch (error) {
      console.error("Error saving layout ID to localStorage:", error);
    }
  }, [selectedLayout]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => {
    // Get the component based on the selected ID using the imported function
    const LayoutComponent = getLayoutComponent(selectedLayout);
    // Get the metadata for the selected layout using the imported function
    const currentLayoutData = getLayoutById(selectedLayout);

    return {
      layouts, // The array of layout metadata objects
      selectedLayout, // The ID of the selected layout
      setSelectedLayout, // Function to change the selected layout ID
      LayoutComponent, // The actual React component for the selected layout
      currentLayoutData, // Metadata for the currently selected layout
    };
    // Ensure dependencies cover everything used inside useMemo
  }, [layouts, selectedLayout]);

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  // Add default values or checks if needed before returning
  // Example: Ensure LayoutComponent is always defined, even if briefly null during loading
  return {
    ...context,
    LayoutComponent: context.LayoutComponent || (() => null), // Provide a fallback component
    currentLayoutData: context.currentLayoutData || getLayoutById(DEFAULT_LAYOUT_ID), // Provide fallback data
  };
};

export default { LayoutContext, LayoutProvider, useLayout }; 